import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowUpRight, FilePdf, CircleNotch } from "@phosphor-icons/react";
import { listProducts } from "@/lib/api";

export default function ProductsPage() {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    listProducts()
      .then((r) => setProducts(r.items || []))
      .catch((e) => setError(e.message))
      .finally(() => setLoading(false));
  }, []);

  return (
    <div data-testid="products-page" className="pt-[140px] pb-20">
      <div className="max-w-[1480px] mx-auto px-5 sm:px-8 lg:px-12">
        <div className="overline mb-6">Catalogo prodotti</div>
        <h1 className="font-display text-[clamp(2.8rem,7vw,6.5rem)] leading-[0.9] tracking-tighter font-medium max-w-4xl">
          Materiali, sistemi, <em className="italic font-light text-[#EB0D0D]">dettagli</em>.
        </h1>
        <p className="mt-8 text-lg md:text-xl text-[#5C5A55] max-w-2xl leading-relaxed">
          Tutti i nostri sistemi sono progettati con i migliori brand europei. Concessionari ufficiali Korus, partner Gibus.
        </p>

        {loading && (
          <div className="flex justify-center py-24">
            <CircleNotch size={32} weight="bold" className="animate-spin text-[#EB0D0D]" />
          </div>
        )}

        {!loading && error && (
          <div className="mt-20 bg-[#F0EFEB] border border-[#EB0D0D]/30 p-8 text-center text-[#5C5A55]">
            Errore nel caricamento dei prodotti. Riprova più tardi.
          </div>
        )}

        {!loading && !error && products.length === 0 && (
          <div className="mt-20 bg-[#F0EFEB] border border-[#D1CFC7]/40 p-12 text-center text-[#5C5A55]">
            Catalogo in aggiornamento.
          </div>
        )}

        {!loading && !error && products.length > 0 && (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 lg:gap-8 mt-20">
            {products.map((p, i) => (
              <article
                key={p.id}
                data-testid={`product-card-${i}`}
                className="group bg-[#F0EFEB] border border-[#D1CFC7]/40 overflow-hidden flex flex-col"
              >
                <div className="relative aspect-[4/3] overflow-hidden bg-[#E2E0D8]">
                  {p.image_url && (
                    <img
                      src={p.image_url}
                      alt={p.title}
                      className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                    />
                  )}
                  {p.badge && (
                    <span className="absolute top-4 left-4 bg-[#1A1918] text-[#FAF9F6] text-xs uppercase tracking-[0.15em] font-bold px-3 py-1.5">
                      {p.badge}
                    </span>
                  )}
                </div>
                <div className="p-7 flex-1 flex flex-col">
                  {p.subtitle && (
                    <div className="text-xs uppercase tracking-[0.18em] text-[#EB0D0D] mb-2">
                      {p.subtitle}
                    </div>
                  )}
                  <h3 className="font-display text-2xl tracking-tight mb-3">{p.title}</h3>
                  {p.description && (
                    <p className="text-[#5C5A55] leading-relaxed flex-1">{p.description}</p>
                  )}
                  <div className="flex flex-wrap items-center gap-5 mt-6">
                    {p.brochure_url && (
                      <a
                        href={p.brochure_url}
                        target="_blank"
                        rel="noopener noreferrer"
                        data-testid={`product-brochure-${i}`}
                        className="inline-flex items-center gap-2 text-sm font-semibold uppercase tracking-[0.15em] text-[#1A1918] hover:text-[#EB0D0D] transition-colors"
                      >
                        <FilePdf size={16} weight="duotone" />
                        Brochure
                      </a>
                    )}
                    <Link
                      to={`/contatti?prodotto=${encodeURIComponent(p.title)}`}
                      data-testid={`product-preventivo-${i}`}
                      className="inline-flex items-center gap-2 text-sm font-semibold uppercase tracking-[0.15em] text-[#EB0D0D] hover:text-[#C00A0A] transition-colors"
                    >
                      Scopri di più
                      <ArrowUpRight size={16} weight="bold" />
                    </Link>
                  </div>
                </div>
              </article>
            ))}
          </div>
        )}

        <div className="mt-28 bg-[#1A1918] text-[#FAF9F6] p-10 md:p-16 lg:p-20 grid grid-cols-1 md:grid-cols-2 gap-10 items-center">
          <div>
            <div className="text-xs uppercase tracking-[0.25em] text-[#EB0D0D] mb-5">Non sai da dove iniziare?</div>
            <h2 className="font-display text-4xl md:text-5xl tracking-tighter leading-[0.95] font-medium">
              Lasciaci consigliarti.
            </h2>
          </div>
          <div className="flex flex-wrap gap-4 md:justify-end">
            <Link to="/contatti" data-testid="products-cta-preventivatore" className="btn-primary">Richiedi Preventivo <ArrowUpRight size={16} weight="bold" /></Link>
            <a href="tel:+393471651098" data-testid="products-cta-tecnico" className="btn-outline" style={{borderColor: '#FAF9F6', color: '#FAF9F6'}}>Parla con un tecnico</a>
          </div>
        </div>
      </div>
    </div>
  );
}
